import React from 'react';
import { FormControl, FormGroup, FormLabel } from '@mui/material';

import { EntitySnapTargetType } from '../../../../../library/layout-snapping/EntitySnapTarget';
import { EntitySnapTargetSelectionSettings } from '../../../../../library/layout-snapping/settings';
import { SnappingSettingsCheckbox } from './SnappingSettingsCheckbox';

interface Props {
  settings: EntitySnapTargetSelectionSettings;
  onTargetToggled(target: EntitySnapTargetType): void;

  disabled?: boolean;
  disabledTypes?: readonly EntitySnapTargetType[];
}

export function SnappingTargetControls({ settings, onTargetToggled, disabled, disabledTypes }: Props) {
  function isDisabled(target: EntitySnapTargetType) {
    return disabled || disabledTypes?.includes(target) === true;
  }

  function isHidden(target: EntitySnapTargetType) {
    return disabledTypes?.includes(target) === true;
  }

  return (
    <FormControl>
      <FormLabel>Snap Against</FormLabel>
      <FormGroup>
        <SnappingSettingsCheckbox
          label="Roof"
          tooltip={isHidden('roof') ? 'Disabled because roofs are hidden.' : 'Snapping will consider all roof lines.'}
          disabled={isDisabled('roof')}
          checked={settings.enabledTargetTypes.includes('roof')}
          onChange={() => {
            onTargetToggled('roof');
          }}
        />

        <SnappingSettingsCheckbox
          label="Ceiling"
          tooltip={
            isHidden('ceiling') ? 'Disabled because ceilings are hidden.' : 'Snapping will consider all ceiling lines.'
          }
          disabled={isDisabled('ceiling')}
          checked={settings.enabledTargetTypes.includes('ceiling')}
          onChange={() => {
            onTargetToggled('ceiling');
          }}
        />

        <SnappingSettingsCheckbox
          label="Truss Envelopes"
          tooltip={
            isHidden('truss-envelope')
              ? 'Disabled because truss envelopes are hidden.'
              : 'Snapping will consider the front, back, and center lines of truss envelopes.'
          }
          disabled={isDisabled('truss-envelope')}
          checked={settings.enabledTargetTypes.includes('truss-envelope')}
          onChange={() => {
            onTargetToggled('truss-envelope');
          }}
        />

        <SnappingSettingsCheckbox
          label="Structural Walls"
          tooltip={
            isHidden('structural-wall')
              ? 'Disabled because structural walls are hidden.'
              : 'Snapping will consider the edges of structural walls.'
          }
          disabled={isDisabled('structural-wall')}
          checked={settings.enabledTargetTypes.includes('structural-wall')}
          onChange={() => {
            onTargetToggled('structural-wall');
          }}
        />

        <SnappingSettingsCheckbox
          label="Non-Structural Walls"
          tooltip={
            isHidden('non-structural-wall')
              ? 'Disabled because non-structural walls are hidden.'
              : 'Snapping will consider the edges of non-structural walls.'
          }
          disabled={isDisabled('non-structural-wall')}
          checked={settings.enabledTargetTypes.includes('non-structural-wall')}
          onChange={() => {
            onTargetToggled('non-structural-wall');
          }}
        />

        <SnappingSettingsCheckbox
          label="Beams"
          tooltip={
            isHidden('beam-bearing-envelope')
              ? 'Disabled because beams are hidden.'
              : 'Snapping will consider the edges of beams.'
          }
          disabled={isDisabled('beam-bearing-envelope')}
          checked={settings.enabledTargetTypes.includes('beam-bearing-envelope')}
          onChange={() => {
            onTargetToggled('beam-bearing-envelope');
          }}
        />

        <SnappingSettingsCheckbox
          label="Work Lines"
          tooltip={
            isHidden('work-line') ? 'Disabled because work lines are hidden.' : 'Snapping will consider all work lines.'
          }
          disabled={isDisabled('work-line')}
          checked={settings.enabledTargetTypes.includes('work-line')}
          onChange={() => {
            onTargetToggled('work-line');
          }}
        />

        <SnappingSettingsCheckbox
          label="Uploaded Lines"
          tooltip={
            isHidden('uploaded-line')
              ? 'Disabled because uploaded lines are hidden.'
              : 'Snapping will consider lines from uploaded DXF or PDF files.'
          }
          disabled={isDisabled('uploaded-line')}
          checked={settings.enabledTargetTypes.includes('uploaded-line')}
          onChange={() => {
            onTargetToggled('uploaded-line');
          }}
        />

        <SnappingSettingsCheckbox
          label="Feature Pattern Containers"
          tooltip={
            isHidden('feature-pattern-container-edge')
              ? 'Disabled because feature pattern containers are hidden.'
              : 'Snapping will consider the edges of feature pattern containers.'
          }
          disabled={isDisabled('feature-pattern-container-edge')}
          checked={settings.enabledTargetTypes.includes('feature-pattern-container-edge')}
          onChange={() => {
            onTargetToggled('feature-pattern-container-edge');
          }}
        />
      </FormGroup>
    </FormControl>
  );
}
